import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { DataTable } from "@/components/app/DataTable";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";

const FIXED = ["Approved LMs change only through a revision with a reason; approval is then asked again.",
  "Changing the original committed delivery date on a submitted LM needs the LM Approve permission.",
  "LM types that don't need approval are approved automatically on submit."];

function Rules() {
  const { data, reload } = useFetch("/settings/approval");
  if (!data) return null;
  const set = async (k, v) => {
    try { await api.put("/settings/approval", { block_self_approval: data.block_self_approval, reject_needs_comment: data.reject_needs_comment, [k]: v }); toast.success("Approval rules saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3">
      <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3" data-testid="approval-rules">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Rules</div>
        {[["block_self_approval", "Requester cannot approve their own LM or design"], ["reject_needs_comment", "Rejecting needs a comment"]].map(([k, l]) => (
          <label key={k} className="flex items-center justify-between gap-3 text-sm"><span>{l}</span><Switch checked={data[k]} onCheckedChange={(v) => set(k, v)} data-testid={`approval-rule-${k}`} /></label>))}
        <ul className="space-y-1.5 text-xs text-slate-600 border-t border-slate-100 pt-2">{FIXED.map((r) => <li key={r} className="flex gap-2"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />{r}</li>)}</ul>
      </div>
      <div className="rounded-md border border-slate-200 bg-white p-4" data-testid="approval-holders">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Who can approve what</div>
        <table className="w-full text-xs"><tbody>{data.who.map((w) => <tr key={w.perm} className="border-b border-slate-100 last:border-0"><td className="py-1.5 pr-2">{w.what}</td><td className="py-1.5 font-medium">{w.roles.join(", ") || "—"}</td></tr>)}</tbody></table>
        <p className="text-xs text-slate-500 mt-2">Anyone holding the approve permission approves all LM types. Change holders under Permissions.</p>
      </div>
    </div>
  );
}

export function ApprovalRules() {
  const { data: types, reload } = useFetch("/lm-types");
  const toggle = async (t) => {
    const { prefix, description, format, projection_by_default, active } = t;
    try { await api.put(`/lm-types/${t.id}`, { prefix, description, format, projection_by_default, active, needs_approval: !t.needs_approval }); toast.success(`${t.prefix} updated`); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const cols = [{ key: "prefix", label: "LM type", mono: true }, { key: "description", label: "Description", wrap: true },
    { key: "needs_approval", label: "Needs approval", exp: (r) => (r.needs_approval ? "Yes" : "No"),
      render: (r) => <span className="flex items-center gap-2"><Switch checked={r.needs_approval} onCheckedChange={() => toggle(r)} data-testid={`approval-toggle-${r.prefix}`} /><span className="text-xs text-slate-500">{r.needs_approval ? "Approver must approve" : "Auto-approved on submit"}</span></span> },
    { key: "active", label: "Active", exp: (r) => (r.active ? "Yes" : "No"), render: (r) => (r.active ? "Yes" : "No") }];
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_400px]" data-testid="admin-approvals">
      <div className="min-w-0"><h3 className="text-sm font-semibold mb-2">Approval by LM type</h3>
        <DataTable testid="approval-lm-types-table" columns={cols} rows={types} rowId={(r) => r.prefix} exportName="Approval_by_LM_type" /></div>
      <Rules />
    </div>
  );
}
